import React, { useEffect, useMemo, useRef, useState } from 'react'

import {
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    Link,
    Modal,
    Paper,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import CancelIcon from '@material-ui/icons/Cancel'
import MailIcon from '@material-ui/icons/Mail'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import SendIcon from '@material-ui/icons/Send'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SearchIcon from '@material-ui/icons/Search'

import { Container } from '../../components'
import {
    TenantMessage,
    MessageRecipient,
    DeliveryStatus,
    ThreadReply,
} from '../../models/TenantMessage'
import {
    useTenantMessageRecipients,
    useTenantMessageReplies,
    useReplyToMessage,
    useMarkRepliesRead,
} from '../../hooks/useTenantMessages'
import { SEND_TENANT_MESSAGES } from '../../models/Permissions'
import { hasPermission } from '../../models/Users/services'
import { useUser } from '../../hooks'
import { useTenantMessageStyles } from './styles'

interface Props {
    open: boolean
    message: TenantMessage | null
    handleClose: () => void
}

const formatDate = (iso: string | null): string => {
    if (!iso) return 'Not yet'
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

const formatReplyDate = (iso: string): string => {
    const d = new Date(iso)
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

const DELIVERY_STATUS_CONFIG: Record<
    DeliveryStatus,
    { label: string; color: string; bg: string } | null
> = {
    NOT_APPLICABLE: null,
    PENDING: { label: 'Pending', color: '#d97706', bg: '#fef3c7' },
    SENT: { label: 'Sent', color: '#059669', bg: '#d1fae5' },
    FAILED: { label: 'Failed', color: '#dc2626', bg: '#fee2e2' },
    SKIPPED: { label: 'Skipped', color: '#6b7280', bg: '#f3f4f6' },
}

const DeliveryStatusChip: React.FC<{
    status: DeliveryStatus
    error?: string | null
}> = ({ status, error }) => {
    const config = DELIVERY_STATUS_CONFIG[status]
    if (!config)
        return (
            <Typography variant="caption" color="textSecondary">
                &mdash;
            </Typography>
        )

    const chip = (
        <Chip
            label={config.label}
            size="small"
            style={{
                backgroundColor: config.bg,
                color: config.color,
                fontWeight: 600,
                fontSize: '0.625rem',
                height: 20,
            }}
        />
    )

    if (status === 'FAILED' && error) {
        return <Tooltip title={error}>{chip}</Tooltip>
    }
    return chip
}

const ReplyBubble: React.FC<{ reply: ThreadReply }> = ({ reply }) => {
    const isStaff = reply.sender_type === 'STAFF'
    const initials = reply.sender_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()

    return (
        <Box
            display="flex"
            flexDirection={isStaff ? 'row-reverse' : 'row'}
            alignItems="flex-start"
            mb={2}
            style={{ gap: 10 }}
        >
            <Box
                style={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    backgroundColor: isStaff ? '#008C85' : '#6b7280',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    flexShrink: 0,
                }}
            >
                {initials}
            </Box>

            <Box style={{ maxWidth: '72%', minWidth: 100 }}>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent={isStaff ? 'flex-end' : 'flex-start'}
                    mb={0.25}
                    style={{ gap: 6 }}
                >
                    <Typography
                        variant="caption"
                        style={{
                            fontWeight: 600,
                            color: isStaff ? '#008C85' : '#374151',
                            fontSize: '0.6875rem',
                        }}
                    >
                        {reply.sender_name}
                    </Typography>
                    <Typography
                        variant="caption"
                        style={{ color: '#9ca3af', fontSize: '0.5625rem' }}
                    >
                        {formatReplyDate(reply.created_date)}
                    </Typography>
                </Box>
                <Box
                    style={{
                        padding: '10px 14px',
                        borderRadius: isStaff
                            ? '14px 4px 14px 14px'
                            : '4px 14px 14px 14px',
                        backgroundColor: isStaff ? '#e8f5f3' : '#f4f5f7',
                        border: isStaff
                            ? '1px solid #c8ece8'
                            : '1px solid #e8eaed',
                    }}
                >
                    <Typography
                        variant="body2"
                        style={{
                            whiteSpace: 'pre-wrap',
                            lineHeight: 1.5,
                            fontSize: '0.8125rem',
                        }}
                    >
                        {reply.body}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

interface TenantThreadViewProps {
    message: TenantMessage
    tenant: MessageRecipient
    onBack: () => void
    canReply: boolean
}

const TenantThreadView: React.FC<TenantThreadViewProps> = ({
    message,
    tenant,
    onBack,
    canReply,
}) => {
    const { replies, isLoading: repliesLoading } = useTenantMessageReplies(
        message.id,
        tenant.tenant,
    )
    const replyMutation = useReplyToMessage()
    const [replyBody, setReplyBody] = useState('')
    const repliesEndRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (repliesEndRef.current) {
            repliesEndRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }, [replies.length])

    const handleSendReply = () => {
        if (!replyBody.trim()) return
        replyMutation.mutate(
            {
                messageId: message.id,
                body: replyBody.trim(),
                recipient: tenant.tenant,
            },
            { onSuccess: () => setReplyBody('') },
        )
    }

    return (
        <Box display="flex" flexDirection="column" style={{ height: '100%' }}>
            {/* Thread header */}
            <Box
                display="flex"
                alignItems="center"
                style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid #e6e9ed',
                    gap: 10,
                }}
            >
                <IconButton size="small" onClick={onBack}>
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box flex={1}>
                    <Typography
                        variant="subtitle2"
                        style={{ fontWeight: 600, lineHeight: 1.2 }}
                    >
                        {tenant.tenant_name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                        {tenant.unit_label} &middot; {tenant.tenant_email}
                    </Typography>
                </Box>
                {replies.length > 0 && (
                    <Chip
                        label={`${replies.length} message${
                            replies.length !== 1 ? 's' : ''
                        }`}
                        size="small"
                        style={{
                            height: 20,
                            fontSize: '0.625rem',
                            fontWeight: 500,
                            backgroundColor: '#f3f4f6',
                        }}
                    />
                )}
            </Box>

            {/* Original message context */}
            <Box
                style={{
                    padding: '12px 16px',
                    backgroundColor: '#f9fafb',
                    borderBottom: '1px solid #e6e9ed',
                }}
            >
                <Typography
                    variant="caption"
                    style={{
                        fontWeight: 600,
                        color: '#6b7280',
                        textTransform: 'uppercase',
                        fontSize: '0.5625rem',
                        letterSpacing: '0.05em',
                    }}
                >
                    Original Message
                </Typography>
                <Typography
                    variant="body2"
                    style={{ fontWeight: 600, marginTop: 4 }}
                >
                    {message.subject}
                </Typography>
                <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        marginTop: 2,
                    }}
                >
                    {message.body}
                </Typography>
            </Box>

            {/* Replies thread */}
            <Box flex={1} style={{ overflow: 'auto', padding: '16px 12px' }}>
                {repliesLoading ? (
                    <Container
                        alignItems="center"
                        justifyContent="center"
                        style={{ height: 120 }}
                    >
                        <CircularProgress size={24} />
                    </Container>
                ) : replies.length === 0 ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        style={{
                            height: '100%',
                            minHeight: 120,
                            padding: '24px 16px',
                        }}
                    >
                        <ChatBubbleOutlineIcon
                            style={{
                                fontSize: 32,
                                color: '#d1d5db',
                                marginBottom: 8,
                            }}
                        />
                        <Typography variant="body2" color="textSecondary">
                            No replies in this thread
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            {canReply
                                ? 'Send the first reply below'
                                : 'Replies will appear here'}
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {replies.map((reply) => (
                            <ReplyBubble key={reply.id} reply={reply} />
                        ))}
                        <div ref={repliesEndRef} />
                    </>
                )}
            </Box>

            {/* Compose bar */}
            {canReply && (
                <Box
                    style={{
                        padding: '12px 16px',
                        borderTop: '1px solid #e6e9ed',
                        backgroundColor: '#fff',
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="flex-end"
                        style={{
                            gap: 10,
                            padding: '8px 12px',
                            borderRadius: 10,
                            border: '1px solid #e0e3e8',
                            backgroundColor: '#fafbfc',
                        }}
                    >
                        <TextField
                            variant="standard"
                            placeholder={`Reply to ${tenant.tenant_name}...`}
                            multiline
                            rows={1}
                            rowsMax={4}
                            value={replyBody}
                            onChange={(e) => setReplyBody(e.target.value)}
                            style={{ flex: 1 }}
                            InputProps={{
                                disableUnderline: true,
                                style: { fontSize: '0.8125rem' },
                            }}
                            onKeyDown={(e) => {
                                if (
                                    e.key === 'Enter' &&
                                    (e.ctrlKey || e.metaKey)
                                ) {
                                    handleSendReply()
                                }
                            }}
                        />
                        <Tooltip title="Send (Ctrl+Enter)">
                            <span>
                                <IconButton
                                    size="small"
                                    disabled={
                                        !replyBody.trim() ||
                                        replyMutation.isLoading
                                    }
                                    onClick={handleSendReply}
                                    style={{
                                        backgroundColor: replyBody.trim()
                                            ? '#008C85'
                                            : undefined,
                                        color: replyBody.trim()
                                            ? '#fff'
                                            : undefined,
                                        width: 30,
                                        height: 30,
                                    }}
                                >
                                    <SendIcon style={{ fontSize: 14 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

export const RecipientsModal: React.FC<Props> = ({
    open,
    message,
    handleClose,
}) => {
    const classes = useTenantMessageStyles()
    const { workspaceUser } = useUser()
    const canSendMessages = hasPermission(workspaceUser, SEND_TENANT_MESSAGES)

    const { recipients, count, isLoading } = useTenantMessageRecipients(
        message?.id ?? null,
    )
    const markReadMutation = useMarkRepliesRead()

    const [
        selectedTenant,
        setSelectedTenant,
    ] = useState<MessageRecipient | null>(null)
    const [recipientSearch, setRecipientSearch] = useState('')
    const [recipientFilter, setRecipientFilter] = useState<
        'all' | 'read' | 'unread' | 'has_replies' | 'unread_replies'
    >('all')
    const [recipientPage, setRecipientPage] = useState(1)
    const RECIPIENTS_PER_PAGE = 25

    useEffect(() => {
        if (open && message?.id) {
            markReadMutation.mutate(message.id)
        }
    }, [open, message?.id])

    useEffect(() => {
        if (!open) {
            setSelectedTenant(null)
            setRecipientSearch('')
            setRecipientFilter('all')
            setRecipientPage(1)
        }
    }, [open])

    const replyCountsByTenant = useMemo(() => {
        const map: Record<number, number> = {}
        recipients.forEach((r) => {
            map[r.tenant] = r.reply_count ?? 0
        })
        return map
    }, [recipients])

    const filteredRecipients = useMemo(() => {
        let result = recipients

        if (recipientFilter === 'read') {
            result = result.filter((r) => r.is_read)
        } else if (recipientFilter === 'unread') {
            result = result.filter((r) => !r.is_read)
        } else if (recipientFilter === 'has_replies') {
            result = result.filter(
                (r) =>
                    (r.reply_count ?? replyCountsByTenant[r.tenant] ?? 0) > 0,
            )
        } else if (recipientFilter === 'unread_replies') {
            result = result.filter((r) => (r.reply_count ?? 0) > 0)
        }

        if (recipientSearch.trim()) {
            const q = recipientSearch.toLowerCase()
            result = result.filter(
                (r) =>
                    r.tenant_name.toLowerCase().includes(q) ||
                    r.tenant_email?.toLowerCase().includes(q) ||
                    r.unit_label?.toLowerCase().includes(q),
            )
        }

        return result
    }, [recipients, recipientSearch, recipientFilter, replyCountsByTenant])

    const totalRecipientPages = Math.ceil(
        filteredRecipients.length / RECIPIENTS_PER_PAGE,
    )
    const paginatedRecipients = filteredRecipients.slice(
        (recipientPage - 1) * RECIPIENTS_PER_PAGE,
        recipientPage * RECIPIENTS_PER_PAGE,
    )

    const showEmail = message?.send_email ?? false
    const showSms = message?.send_sms ?? false

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Slide direction="up" in={open}>
                <Paper className={classes.paper}>
                    <Container
                        direction="column"
                        style={{
                            width: 'min(1200px, calc(100vw - 60px))',
                            height: 'calc(100vh - 80px)',
                            maxHeight: 900,
                            overflow: 'hidden',
                        }}
                    >
                        {/* Header */}
                        <div className={classes.header}>
                            <div className={classes.headerIcon}>
                                <MailIcon />
                            </div>
                            <Container direction="column" flex={1}>
                                <span className={classes.headerTitle}>
                                    {message?.subject ?? 'Message Detail'}
                                </span>
                                <span className={classes.headerSubtitle}>
                                    {count} recipient{count !== 1 ? 's' : ''}
                                    {showEmail && ' · Email enabled'}
                                    {showSms && ' · SMS enabled'}
                                    {message?.reply_count
                                        ? ` · ${message.reply_count} replies`
                                        : ''}
                                </span>
                            </Container>
                            <IconButton size="small" onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </div>

                        {/* Message preview */}
                        {message && !selectedTenant && (
                            <div
                                style={{
                                    padding: '16px 24px',
                                    borderBottom: '1px solid #e6e9ed',
                                    backgroundColor: '#fff',
                                }}
                            >
                                <Container
                                    alignItems="center"
                                    style={{ gap: 8, marginBottom: 8 }}
                                >
                                    <Chip
                                        label={
                                            message.audience_type === 'ALL'
                                                ? 'All Tenants'
                                                : `${message.recipient_count} selected`
                                        }
                                        size="small"
                                        color={
                                            message.audience_type === 'ALL'
                                                ? 'primary'
                                                : 'default'
                                        }
                                        variant={
                                            message.audience_type === 'ALL'
                                                ? 'default'
                                                : 'outlined'
                                        }
                                    />
                                    {message.initiated_by_tenant && (
                                        <Chip
                                            label="Tenant Initiated"
                                            size="small"
                                            style={{
                                                height: 20,
                                                fontSize: '0.625rem',
                                                fontWeight: 600,
                                                backgroundColor: '#ede9fe',
                                                color: '#7c3aed',
                                            }}
                                        />
                                    )}
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                    >
                                        Sent by {message.created_by_name}
                                    </Typography>
                                </Container>
                                <Typography
                                    variant="body2"
                                    style={{ whiteSpace: 'pre-wrap' }}
                                >
                                    {message.body}
                                </Typography>
                                {message.attachments &&
                                    message.attachments.length > 0 && (
                                        <Box
                                            mt={1.5}
                                            display="flex"
                                            flexWrap="wrap"
                                            gridGap={8}
                                        >
                                            {message.attachments.map((att) => (
                                                <Link
                                                    key={att.id}
                                                    href={att.file_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    underline="none"
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 4,
                                                        padding: '4px 10px',
                                                        borderRadius: 6,
                                                        border:
                                                            '1px solid #e6e9ed',
                                                        backgroundColor:
                                                            '#fafbfc',
                                                        fontSize: '0.75rem',
                                                        color: '#374151',
                                                    }}
                                                >
                                                    <AttachFileIcon
                                                        style={{ fontSize: 14 }}
                                                    />
                                                    {att.name}
                                                </Link>
                                            ))}
                                        </Box>
                                    )}
                            </div>
                        )}

                        {/* Main content */}
                        {selectedTenant && message ? (
                            <TenantThreadView
                                message={message}
                                tenant={selectedTenant}
                                onBack={() => setSelectedTenant(null)}
                                canReply={canSendMessages}
                            />
                        ) : (
                            <div
                                style={{
                                    flex: 1,
                                    overflow: 'auto',
                                    padding: 16,
                                }}
                            >
                                {isLoading ? (
                                    <Container
                                        alignItems="center"
                                        justifyContent="center"
                                        flex={1}
                                        style={{ height: 200 }}
                                    >
                                        <CircularProgress />
                                    </Container>
                                ) : recipients.length === 0 ? (
                                    <Container
                                        alignItems="center"
                                        justifyContent="center"
                                        flex={1}
                                        style={{ height: 200 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                        >
                                            No recipients found
                                        </Typography>
                                    </Container>
                                ) : (
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        style={{ height: '100%' }}
                                    >
                                        {/* Search */}
                                        <Box style={{ padding: '0 0 12px' }}>
                                            <TextField
                                                size="small"
                                                variant="outlined"
                                                placeholder="Search recipients..."
                                                fullWidth
                                                value={recipientSearch}
                                                onChange={(e) => {
                                                    setRecipientSearch(
                                                        e.target.value,
                                                    )
                                                    setRecipientPage(1)
                                                }}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon
                                                                fontSize="small"
                                                                color="disabled"
                                                            />
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        </Box>

                                        {/* Quick filters */}
                                        <Box
                                            display="flex"
                                            style={{
                                                gap: 6,
                                                paddingBottom: 12,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            {([
                                                'all',
                                                'read',
                                                'unread',
                                                'has_replies',
                                                'unread_replies',
                                            ] as const).map((filter) => {
                                                const labels: Record<
                                                    typeof filter,
                                                    string
                                                > = {
                                                    all: 'All',
                                                    read: 'Read',
                                                    unread: 'Unread',
                                                    has_replies: 'Has Replies',
                                                    unread_replies:
                                                        'Tenant Replies',
                                                }
                                                const isActive =
                                                    recipientFilter === filter
                                                return (
                                                    <Chip
                                                        key={filter}
                                                        label={labels[filter]}
                                                        size="small"
                                                        onClick={() => {
                                                            setRecipientFilter(
                                                                filter,
                                                            )
                                                            setRecipientPage(1)
                                                        }}
                                                        style={{
                                                            height: 26,
                                                            fontSize:
                                                                '0.6875rem',
                                                            fontWeight: isActive
                                                                ? 700
                                                                : 500,
                                                            backgroundColor: isActive
                                                                ? '#008C85'
                                                                : '#f3f4f6',
                                                            color: isActive
                                                                ? '#fff'
                                                                : '#374151',
                                                            cursor: 'pointer',
                                                            border: isActive
                                                                ? 'none'
                                                                : '1px solid #e5e7eb',
                                                        }}
                                                    />
                                                )
                                            })}
                                        </Box>

                                        {filteredRecipients.length === 0 ? (
                                            <Container
                                                alignItems="center"
                                                justifyContent="center"
                                                flex={1}
                                                style={{ height: 120 }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                >
                                                    No recipients match your
                                                    search or filter
                                                </Typography>
                                            </Container>
                                        ) : (
                                            <>
                                                <TableContainer
                                                    style={{ flex: 1 }}
                                                >
                                                    <Table
                                                        size="small"
                                                        stickyHeader
                                                    >
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                >
                                                                    Tenant
                                                                </TableCell>
                                                                <TableCell
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                >
                                                                    Unit
                                                                </TableCell>
                                                                <TableCell
                                                                    align="center"
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                >
                                                                    Read
                                                                </TableCell>
                                                                <TableCell
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                >
                                                                    Read At
                                                                </TableCell>
                                                                {showEmail && (
                                                                    <TableCell
                                                                        style={{
                                                                            fontWeight: 600,
                                                                            fontSize:
                                                                                '0.75rem',
                                                                        }}
                                                                    >
                                                                        Email
                                                                    </TableCell>
                                                                )}
                                                                {showSms && (
                                                                    <TableCell
                                                                        style={{
                                                                            fontWeight: 600,
                                                                            fontSize:
                                                                                '0.75rem',
                                                                        }}
                                                                    >
                                                                        SMS
                                                                    </TableCell>
                                                                )}
                                                                <TableCell
                                                                    align="center"
                                                                    style={{
                                                                        fontWeight: 600,
                                                                        fontSize:
                                                                            '0.75rem',
                                                                    }}
                                                                >
                                                                    Replies
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {paginatedRecipients.map(
                                                                (r) => {
                                                                    const tenantReplyCount =
                                                                        r.reply_count ??
                                                                        replyCountsByTenant[
                                                                            r
                                                                                .tenant
                                                                        ] ??
                                                                        0
                                                                    return (
                                                                        <TableRow
                                                                            key={
                                                                                r.id
                                                                            }
                                                                            hover
                                                                            style={{
                                                                                cursor:
                                                                                    'pointer',
                                                                            }}
                                                                            onClick={() =>
                                                                                setSelectedTenant(
                                                                                    r,
                                                                                )
                                                                            }
                                                                        >
                                                                            <TableCell>
                                                                                <Box>
                                                                                    <Typography
                                                                                        variant="body2"
                                                                                        style={{
                                                                                            fontWeight: 500,
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            r.tenant_name
                                                                                        }
                                                                                    </Typography>
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="textSecondary"
                                                                                        style={{
                                                                                            fontSize:
                                                                                                '0.625rem',
                                                                                        }}
                                                                                    >
                                                                                        {
                                                                                            r.tenant_email
                                                                                        }
                                                                                    </Typography>
                                                                                </Box>
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Typography variant="caption">
                                                                                    {
                                                                                        r.unit_label
                                                                                    }
                                                                                </Typography>
                                                                            </TableCell>
                                                                            <TableCell align="center">
                                                                                {r.is_read ? (
                                                                                    <CheckCircleIcon
                                                                                        style={{
                                                                                            color:
                                                                                                '#4caf50',
                                                                                            fontSize: 18,
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <CancelIcon
                                                                                        style={{
                                                                                            color:
                                                                                                '#bdbdbd',
                                                                                            fontSize: 18,
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Typography variant="caption">
                                                                                    {formatDate(
                                                                                        r.read_at,
                                                                                    )}
                                                                                </Typography>
                                                                            </TableCell>
                                                                            {showEmail && (
                                                                                <TableCell>
                                                                                    <DeliveryStatusChip
                                                                                        status={
                                                                                            r.email_status
                                                                                        }
                                                                                        error={
                                                                                            r.email_error
                                                                                        }
                                                                                    />
                                                                                </TableCell>
                                                                            )}
                                                                            {showSms && (
                                                                                <TableCell>
                                                                                    <DeliveryStatusChip
                                                                                        status={
                                                                                            r.sms_status
                                                                                        }
                                                                                        error={
                                                                                            r.sms_error
                                                                                        }
                                                                                    />
                                                                                </TableCell>
                                                                            )}
                                                                            <TableCell align="center">
                                                                                {tenantReplyCount >
                                                                                0 ? (
                                                                                    <Chip
                                                                                        icon={
                                                                                            <ChatBubbleOutlineIcon
                                                                                                style={{
                                                                                                    fontSize: 12,
                                                                                                }}
                                                                                            />
                                                                                        }
                                                                                        label={
                                                                                            tenantReplyCount
                                                                                        }
                                                                                        size="small"
                                                                                        style={{
                                                                                            height: 20,
                                                                                            fontSize:
                                                                                                '0.625rem',
                                                                                            fontWeight: 600,
                                                                                            backgroundColor:
                                                                                                '#e8f5f3',
                                                                                            color:
                                                                                                '#008C85',
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        color="textSecondary"
                                                                                    >
                                                                                        &mdash;
                                                                                    </Typography>
                                                                                )}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    )
                                                                },
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>

                                                {/* Pagination */}
                                                {totalRecipientPages > 1 && (
                                                    <Box
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="space-between"
                                                        style={{
                                                            padding:
                                                                '10px 0 4px',
                                                            borderTop:
                                                                '1px solid #e6e9ed',
                                                            marginTop: 8,
                                                        }}
                                                    >
                                                        <Typography
                                                            variant="caption"
                                                            color="textSecondary"
                                                        >
                                                            Showing{' '}
                                                            {(recipientPage -
                                                                1) *
                                                                RECIPIENTS_PER_PAGE +
                                                                1}
                                                            &ndash;
                                                            {Math.min(
                                                                recipientPage *
                                                                    RECIPIENTS_PER_PAGE,
                                                                filteredRecipients.length,
                                                            )}{' '}
                                                            of{' '}
                                                            {
                                                                filteredRecipients.length
                                                            }
                                                        </Typography>
                                                        <Box
                                                            display="flex"
                                                            alignItems="center"
                                                            style={{ gap: 8 }}
                                                        >
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                disabled={
                                                                    recipientPage <=
                                                                    1
                                                                }
                                                                onClick={() =>
                                                                    setRecipientPage(
                                                                        (p) =>
                                                                            p -
                                                                            1,
                                                                    )
                                                                }
                                                                style={{
                                                                    textTransform:
                                                                        'none',
                                                                    minWidth: 70,
                                                                    fontSize:
                                                                        '0.75rem',
                                                                }}
                                                            >
                                                                Previous
                                                            </Button>
                                                            <Typography
                                                                variant="caption"
                                                                style={{
                                                                    fontWeight: 500,
                                                                }}
                                                            >
                                                                {recipientPage}{' '}
                                                                /{' '}
                                                                {
                                                                    totalRecipientPages
                                                                }
                                                            </Typography>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                disabled={
                                                                    recipientPage >=
                                                                    totalRecipientPages
                                                                }
                                                                onClick={() =>
                                                                    setRecipientPage(
                                                                        (p) =>
                                                                            p +
                                                                            1,
                                                                    )
                                                                }
                                                                style={{
                                                                    textTransform:
                                                                        'none',
                                                                    minWidth: 70,
                                                                    fontSize:
                                                                        '0.75rem',
                                                                }}
                                                            >
                                                                Next
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Box>
                                )}
                            </div>
                        )}
                    </Container>
                </Paper>
            </Slide>
        </Modal>
    )
}
